import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Typography,
  Paper
} from "@mui/material";
import SimpleDatePicker from "../../../ui/simpleDatePicker";

// Shape contract
// value = {
//   habitualRx: { constantly: bool, near: bool, far: bool },
//   diabetes: 'yes' | 'no' | 'unknown',
//   complaint: string,
//   blur: { near: bool, far: bool, withRx: bool, noRx: bool, details: string },
//   diplopia: { value: bool, details: string },
//   flashesFloaters: { flashes: bool, floaters: bool, details: string },
//   headaches: string,
//   familyHistory: string,
//   ocularHistory: string,
//   medical: string,
//   meds: string,
//   drops: string,
//   allergies: string,
//   lastEyeExamDate: 'YYYY-MM-DD' | '',
//   lastEyeExamDetails: string,
//   lastMedicalDate: 'YYYY-MM-DD' | '',
//   lastMedicalDetails: string,
//   // suggestions (optional)
//   diabetesDurationYears?: string,
//   diabetesLastA1c?: string,
// }

export function getDefaultGeneralValues() {
  return {
    habitualRx: { constantly: false, near: false, far: false },
    diabetes: "no",
    complaint: "",
    blur: { near: false, far: false, withRx: false, noRx: false, details: "" },
    diplopia: { value: false, details: "" },
    flashesFloaters: { flashes: false, floaters: false, details: "" },
    headaches: "",
    familyHistory: "",
    ocularHistory: "",
    medical: "",
    meds: "",
    drops: "",
    allergies: "",
    lastEyeExamDate: "",
    lastEyeExamDetails: "",
    lastMedicalDate: "",
    lastMedicalDetails: "",
    diabetesDurationYears: "",
    diabetesLastA1c: "",
  };
}

export default function GeneralTab({ value, onChange }) {
  const v = value || getDefaultGeneralValues();

  const update = (patch) => onChange?.({ ...v, ...patch });

  const SectionHeader = ({ children }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mb: 1, mt: 2 }}>
      {children}
    </Typography>
  );

  return (
    <Box>
      <Grid container spacing={2}>
        {/* CHIEF COMPLAINT */}
        <Grid item xs={12}>
          <SectionHeader>Chief Complaint</SectionHeader>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Complaint / Symptoms"
            placeholder="none"
            fullWidth
            multiline
            minRows={2}
            value={v.complaint || ""}
            onChange={(e) => update({ complaint: e.target.value })}
          />
        </Grid>

        {/* VISUAL SYMPTOMS */}
        <Grid item xs={12}>
          <SectionHeader>Visual Symptoms</SectionHeader>
        </Grid>

        {/* Habitual Rx */}
        <Grid item xs={12} md={6}>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">Habitual Rx</FormLabel>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!v.habitualRx.constantly}
                    onChange={(e) =>
                      update({ habitualRx: { ...v.habitualRx, constantly: e.target.checked } })
                    }
                  />
                }
                label="Constantly"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!v.habitualRx.near}
                    onChange={(e) => update({ habitualRx: { ...v.habitualRx, near: e.target.checked } })}
                  />
                }
                label="Near"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!v.habitualRx.far}
                    onChange={(e) => update({ habitualRx: { ...v.habitualRx, far: e.target.checked } })}
                  />
                }
                label="Far"
              />
            </FormGroup>
          </FormControl>
        </Grid>

        {/* Blur Near/Far With Rx/No Rx with details */}
        <Grid item xs={12} md={6}>
          <FormControl component="fieldset" variant="standard" sx={{ mb: 1 }}>
            <FormLabel component="legend">Blur</FormLabel>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={!!v.blur.near} onChange={(e) => update({ blur: { ...v.blur, near: e.target.checked } })} />}
                label="Near"
              />
              <FormControlLabel
                control={<Checkbox checked={!!v.blur.far} onChange={(e) => update({ blur: { ...v.blur, far: e.target.checked } })} />}
                label="Far"
              />
              <FormControlLabel
                control={<Checkbox checked={!!v.blur.withRx} onChange={(e) => update({ blur: { ...v.blur, withRx: e.target.checked } })} />}
                label="With Rx"
              />
              <FormControlLabel
                control={<Checkbox checked={!!v.blur.noRx} onChange={(e) => update({ blur: { ...v.blur, noRx: e.target.checked } })} />}
                label="No Rx"
              />
            </FormGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Blur details"
            fullWidth
            multiline
            minRows={2}
            value={v.blur.details || ""}
            onChange={(e) => update({ blur: { ...v.blur, details: e.target.value } })}
          />
        </Grid>

        {/* Diplopia */}
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!v.diplopia.value}
                onChange={(e) => update({ diplopia: { ...v.diplopia, value: e.target.checked } })}
              />
            }
            label="Diplopia"
          />
          {v.diplopia.value && (
            <TextField
              label="Diplopia details"
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 1 }}
              value={v.diplopia.details || ""}
              onChange={(e) => update({ diplopia: { ...v.diplopia, details: e.target.value } })}
            />
          )}
        </Grid>

        {/* Flashes / Floaters */}
        <Grid item xs={12} md={6}>
          <FormGroup row>
            <FormControlLabel
              control={<Checkbox checked={!!v.flashesFloaters.flashes} onChange={(e) => update({ flashesFloaters: { ...v.flashesFloaters, flashes: e.target.checked } })} />}
              label="Flashes"
            />
            <FormControlLabel
              control={<Checkbox checked={!!v.flashesFloaters.floaters} onChange={(e) => update({ flashesFloaters: { ...v.flashesFloaters, floaters: e.target.checked } })} />}
              label="Floaters"
            />
          </FormGroup>
          {(v.flashesFloaters.flashes || v.flashesFloaters.floaters) && (
            <TextField
              label="Flashes/Floaters details"
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 1 }}
              value={v.flashesFloaters.details || ""}
              onChange={(e) => update({ flashesFloaters: { ...v.flashesFloaters, details: e.target.value } })}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Headaches"
            fullWidth
            multiline
            minRows={2}
            value={v.headaches || ""}
            onChange={(e) => update({ headaches: e.target.value })}
          />
        </Grid>

        {/* MEDICAL HISTORY */}
        <Grid item xs={12}>
          <SectionHeader>Medical History</SectionHeader>
        </Grid>

        {/* Diabetes */}
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Diabetes</FormLabel>
            <RadioGroup
              row
              value={v.diabetes || "no"}
              onChange={(e) => update({ diabetes: e.target.value })}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="unknown" control={<Radio />} label="Unknown" />
            </RadioGroup>
          </FormControl>
          {v.diabetes === "yes" && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Diabetes Duration (years)"
                  fullWidth
                  value={v.diabetesDurationYears || ""}
                  onChange={(e) => update({ diabetesDurationYears: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last A1c"
                  fullWidth
                  value={v.diabetesLastA1c || ""}
                  onChange={(e) => update({ diabetesLastA1c: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Medical History"
            fullWidth
            multiline
            minRows={3}
            value={v.medical || ""}
            onChange={(e) => update({ medical: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Ocular History"
            fullWidth
            multiline
            minRows={3}
            value={v.ocularHistory || ""}
            onChange={(e) => update({ ocularHistory: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Family History"
            fullWidth
            multiline
            minRows={3}
            value={v.familyHistory || ""}
            onChange={(e) => update({ familyHistory: e.target.value })}
          />
        </Grid>

        {/* MEDICATIONS & ALLERGIES */}
        <Grid item xs={12}>
          <SectionHeader>Medications & Allergies</SectionHeader>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Medications"
            fullWidth
            multiline
            minRows={3}
            value={v.meds || ""}
            onChange={(e) => update({ meds: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Eye Drops"
            fullWidth
            multiline
            minRows={3}
            value={v.drops || ""}
            onChange={(e) => update({ drops: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Allergies"
            fullWidth
            multiline
            minRows={3}
            placeholder="e.g., Medication: penicillin; Environmental: pollen"
            helperText="Hint: distinguish medication vs environmental; we can separate as tags later."
            value={v.allergies || ""}
            onChange={(e) => update({ allergies: e.target.value })}
          />
        </Grid>

        {/* PRIOR EXAMS */}
        <Grid item xs={12}>
          <SectionHeader>Prior Exams</SectionHeader>
        </Grid>

        {/* Last eye exam and last medical */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>Last Eye Exam</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SimpleDatePicker
                  label="Date"
                  value={v.lastEyeExamDate || ""}
                  onChange={(val) => update({ lastEyeExamDate: val || "" })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Details"
                  fullWidth
                  multiline
                  minRows={3}
                  value={v.lastEyeExamDetails || ""}
                  onChange={(e) => update({ lastEyeExamDetails: e.target.value })}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>Last Medical Exam</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SimpleDatePicker
                  label="Date"
                  value={v.lastMedicalDate || ""}
                  onChange={(val) => update({ lastMedicalDate: val || "" })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Details"
                  fullWidth
                  multiline
                  minRows={3}
                  value={v.lastMedicalDetails || ""}
                  onChange={(e) => update({ lastMedicalDetails: e.target.value })}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
